import { Box3, Group, type Object3D, Vector3 } from "three";
import { type ModelAssetLoader, modelAssetLibrary } from "../models/model-asset-library";
import { disposeObject3DResources as disposeObject } from "../models/shared-model-resources";
import { RackShelf } from "./rack-shelf";

export type CargoCellSize = {
  height: number;
  length: number;
  width: number;
};

export type StandardRackShelfCargoPlacement = {
  heightIndex: number;
  lengthIndex: number;
  slotIndex: number;
};

export type StandardRackShelfCargoCell = Omit<StandardRackShelfCargoPlacement, "slotIndex">;

export type StandardRackShelfGridSize = {
  heightCellCount: number;
  lengthCellCount: number;
};

export type StandardRackShelfConfiguration = StandardRackShelfGridSize & {
  cargoCapacity: number;
  cargoCellSize: CargoCellSize;
};

type StandardRackShelfPosition = {
  x?: number;
  y?: number;
  z?: number;
};

export type StandardRackShelfOptions = {
  bottomBraceY?: number;
  bottomBraceZ?: number;
  cargoCapacity?: number;
  cargoCellSize?: CargoCellSize;
  columnBraceXOffset?: number;
  crossbarZOffset?: number;
  heightCellCount?: number;
  lengthCellCount?: number;
  position?: StandardRackShelfPosition;
  topBraceInset?: number;
};

export const defaultStandardRackShelfCargoCellSize: CargoCellSize = {
  height: 1.3,
  length: 2.3,
  width: 0.88
};
export const defaultStandardRackShelfCargoCapacity = 2;

// Base plate height plus half the crossbar thickness.
const cargoCrossbarTopOffset = 0.05;
const defaultStandardRackShelfOptions = {
  heightCellCount: 1,
  lengthCellCount: 1
};

type StandardRackShelfCargo = StandardRackShelfCargoPlacement & {
  root: Group;
};

export class StandardRackShelf extends Group {
  private readonly cargoGroup = new Group();
  private readonly cargoPlacements = new Map<string, StandardRackShelfCargo>();
  private cargoCapacity: number;
  private cargoCellSize: CargoCellSize;
  private heightCellCount: number;
  private lengthCellCount: number;
  private rackShelf?: RackShelf;
  private updateQueue: Promise<void> = Promise.resolve();
  private readonly rackShelfStructuralOptions: {
    bottomBraceY?: number;
    bottomBraceZ?: number;
    columnBraceXOffset?: number;
    crossbarZOffset?: number;
    topBraceInset?: number;
  };

  constructor({
    bottomBraceY,
    bottomBraceZ,
    cargoCapacity = defaultStandardRackShelfCargoCapacity,
    cargoCellSize = defaultStandardRackShelfCargoCellSize,
    columnBraceXOffset,
    crossbarZOffset,
    heightCellCount = defaultStandardRackShelfOptions.heightCellCount,
    lengthCellCount = defaultStandardRackShelfOptions.lengthCellCount,
    position = {},
    topBraceInset
  }: StandardRackShelfOptions = {}) {
    super();
    this.cargoCapacity = validatePositiveInteger(cargoCapacity, "cargoCapacity");
    this.cargoCellSize = validateCargoCellSize(cargoCellSize);
    this.heightCellCount = validateCellCount(heightCellCount, "heightCellCount");
    this.lengthCellCount = validateCellCount(lengthCellCount, "lengthCellCount");
    this.rackShelfStructuralOptions = {
      bottomBraceY,
      bottomBraceZ,
      columnBraceXOffset,
      crossbarZOffset,
      topBraceInset
    };
    this.name = "standard-rack-shelf";
    this.cargoGroup.name = "standard-rack-shelf-cargo";
    this.add(this.cargoGroup);
    this.position.set(position.x ?? 0, position.y ?? 0, position.z ?? 0);
  }

  static async create(
    options: StandardRackShelfOptions = {},
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const standardRackShelf = new StandardRackShelf(options);
    await standardRackShelf.load(loader);
    return standardRackShelf;
  }

  getCargoCellHitTargets() {
    return this.rackShelf?.getCargoCellHitTargets() ?? [];
  }

  getGridSize(): StandardRackShelfGridSize {
    return {
      heightCellCount: this.heightCellCount,
      lengthCellCount: this.lengthCellCount
    };
  }

  getConfiguration(): StandardRackShelfConfiguration {
    return {
      ...this.getGridSize(),
      cargoCapacity: this.cargoCapacity,
      cargoCellSize: { ...this.cargoCellSize }
    };
  }

  getCargoCell(hitTarget: Object3D): StandardRackShelfCargoCell | undefined {
    if (!this.getCargoCellHitTargets().some((target) => target === hitTarget)) {
      return;
    }

    const cargoCell = hitTarget.userData.rackShelfCargoCell;

    if (
      !cargoCell ||
      !Number.isInteger(cargoCell.heightIndex) ||
      !Number.isInteger(cargoCell.lengthIndex)
    ) {
      return;
    }

    return {
      heightIndex: cargoCell.heightIndex,
      lengthIndex: cargoCell.lengthIndex
    };
  }

  getCargoCellHitTarget({ heightIndex, lengthIndex }: StandardRackShelfCargoCell) {
    return this.getCargoCellHitTargets().find((hitTarget) => {
      const cargoCell = this.getCargoCell(hitTarget);
      return cargoCell?.heightIndex === heightIndex && cargoCell.lengthIndex === lengthIndex;
    });
  }

  showCargoCellHighlight(hitTarget: Object3D) {
    this.rackShelf?.showCargoCellHighlight(hitTarget);
  }

  hideCargoCellHighlight() {
    this.rackShelf?.hideCargoCellHighlight();
  }

  selectCargoCell(hitTarget: Object3D) {
    this.rackShelf?.selectCargoCell(hitTarget);
  }

  clearCargoCellSelection() {
    this.rackShelf?.clearCargoCellSelection();
  }

  setSelected(selected: boolean) {
    this.rackShelf?.setSelected(selected);
  }

  setCargo(placement: StandardRackShelfCargoPlacement, object: Object3D) {
    const cargoPlacement = this.validateCargoPlacement(placement);
    const key = createCargoPlacementKey(cargoPlacement);
    this.removeCargo(cargoPlacement);

    const root = createCargoRoot(object);
    const cargo = { ...cargoPlacement, root };
    this.cargoPlacements.set(key, cargo);
    this.cargoGroup.add(root);
    this.updateCargoPlacement(cargo);
  }

  removeCargo(placement: StandardRackShelfCargoPlacement) {
    const cargoPlacement = this.validateCargoPlacement(placement);
    const key = createCargoPlacementKey(cargoPlacement);
    const cargo = this.cargoPlacements.get(key);

    if (!cargo) {
      return;
    }

    this.cargoGroup.remove(cargo.root);
    this.cargoPlacements.delete(key);
    disposeObject(cargo.root);
  }

  setCargoCellSize(cargoCellSize: CargoCellSize, loader: ModelAssetLoader = modelAssetLibrary) {
    const nextCargoCellSize = validateCargoCellSize(cargoCellSize);

    return this.enqueueUpdate(async () => {
      this.cargoCellSize = nextCargoCellSize;
      await this.applyDimensions(loader);
      this.updateCargoPlacements();
    });
  }

  setCargoCapacity(cargoCapacity: number) {
    this.cargoCapacity = validatePositiveInteger(cargoCapacity, "cargoCapacity");
    this.pruneCargoPlacements();
    this.updateCargoPlacements();
  }

  async setConfiguration(
    configuration: StandardRackShelfConfiguration,
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const nextConfiguration = validateConfiguration(configuration);

    if (nextConfiguration.cargoCapacity !== this.cargoCapacity) {
      this.setCargoCapacity(nextConfiguration.cargoCapacity);
    }
    if (!isSameCargoCellSize(nextConfiguration.cargoCellSize, this.cargoCellSize)) {
      await this.setCargoCellSize(nextConfiguration.cargoCellSize, loader);
    }
    await this.setGridSize(nextConfiguration, loader);
  }

  async setGridSize(
    { heightCellCount, lengthCellCount }: StandardRackShelfGridSize,
    loader: ModelAssetLoader = modelAssetLibrary
  ) {
    const nextHeightCellCount = validateCellCount(heightCellCount, "heightCellCount");
    const nextLengthCellCount = validateCellCount(lengthCellCount, "lengthCellCount");

    if (nextLengthCellCount !== this.lengthCellCount) {
      await this.setLengthCellCount(nextLengthCellCount, loader);
    }
    if (nextHeightCellCount !== this.heightCellCount) {
      await this.setHeightCellCount(nextHeightCellCount, loader);
    }
  }

  setHeightCellCount(heightCellCount: number, loader: ModelAssetLoader = modelAssetLibrary) {
    const nextHeightCellCount = validateCellCount(heightCellCount, "heightCellCount");

    return this.enqueueUpdate(async () => {
      this.heightCellCount = nextHeightCellCount;
      await this.rackShelf?.setHeightCellCount(nextHeightCellCount, loader);
      await this.rackShelf?.setHeight(this.getTotalHeight(), loader);
      this.pruneCargoPlacements();
      this.updateCargoPlacements();
    });
  }

  setLengthCellCount(lengthCellCount: number, loader: ModelAssetLoader = modelAssetLibrary) {
    const nextLengthCellCount = validateCellCount(lengthCellCount, "lengthCellCount");

    return this.enqueueUpdate(async () => {
      this.lengthCellCount = nextLengthCellCount;
      await this.rackShelf?.setLengthCellCount(nextLengthCellCount, loader);
      await this.rackShelf?.setLength(this.getTotalLength(), loader);
      this.pruneCargoPlacements();
      this.updateCargoPlacements();
    });
  }

  setShelfPosition(position: StandardRackShelfPosition) {
    this.position.set(
      position.x ?? this.position.x,
      position.y ?? this.position.y,
      position.z ?? this.position.z
    );
    this.refreshRackShelfWorldState();
  }

  setShelfRotationY(rotationY: number) {
    if (!Number.isFinite(rotationY)) {
      throw new RangeError("rotationY must be finite");
    }

    this.rotation.y = rotationY;
    this.refreshRackShelfWorldState();
  }

  private async load(loader: ModelAssetLoader) {
    const rackShelf = await RackShelf.create(
      {
        ...this.rackShelfStructuralOptions,
        height: this.getTotalHeight(),
        heightCellCount: this.heightCellCount,
        length: this.getTotalLength(),
        lengthCellCount: this.lengthCellCount,
        width: this.cargoCellSize.width
      },
      loader
    );

    this.rackShelf = rackShelf;
    this.add(rackShelf);
    this.refreshRackShelfWorldState();
  }

  private enqueueUpdate(update: () => Promise<void>) {
    const nextUpdate = this.updateQueue.then(update);
    this.updateQueue = nextUpdate.catch(() => undefined);
    return nextUpdate;
  }

  private async applyDimensions(loader: ModelAssetLoader) {
    await this.rackShelf?.setLength(this.getTotalLength(), loader);
    await this.rackShelf?.setHeight(this.getTotalHeight(), loader);
    await this.rackShelf?.setWidth(this.cargoCellSize.width, loader);
  }

  private getTotalHeight() {
    return this.cargoCellSize.height * this.heightCellCount;
  }

  private getTotalLength() {
    return this.cargoCellSize.length * this.lengthCellCount;
  }

  private pruneCargoPlacements() {
    this.cargoPlacements.forEach((cargo, key) => {
      if (cargo.heightIndex >= this.heightCellCount || cargo.lengthIndex >= this.lengthCellCount) {
        this.cargoGroup.remove(cargo.root);
        this.cargoPlacements.delete(key);
        disposeObject(cargo.root);
        return;
      }

      if (cargo.slotIndex >= this.cargoCapacity) {
        this.cargoGroup.remove(cargo.root);
        this.cargoPlacements.delete(key);
        disposeObject(cargo.root);
      }
    });
  }

  private updateCargoPlacements() {
    this.cargoPlacements.forEach((cargo) => {
      this.updateCargoPlacement(cargo);
    });
  }

  private updateCargoPlacement(cargo: StandardRackShelfCargo) {
    const slotCenter = (cargo.slotIndex + 0.5) / this.cargoCapacity;
    const supportSurfaceOffset = cargo.heightIndex > 0 ? cargoCrossbarTopOffset : 0;

    cargo.root.position.set(
      this.cargoCellSize.length * (cargo.lengthIndex + slotCenter),
      this.cargoCellSize.height * cargo.heightIndex + supportSurfaceOffset,
      this.cargoCellSize.width / 2
    );
  }

  private validateCargoPlacement({
    heightIndex,
    lengthIndex,
    slotIndex
  }: StandardRackShelfCargoPlacement): StandardRackShelfCargoPlacement {
    validateCellIndex(heightIndex, this.heightCellCount, "heightIndex");
    validateCellIndex(lengthIndex, this.lengthCellCount, "lengthIndex");
    validateSlotIndex(slotIndex, this.cargoCapacity);

    return { heightIndex, lengthIndex, slotIndex };
  }

  private refreshRackShelfWorldState() {
    this.rackShelf?.setShelfPosition({ x: 0, y: 0, z: 0 });
  }
}

function validateCargoCellSize({ height, length, width }: CargoCellSize): CargoCellSize {
  validatePositiveNumber(height, "cargoCellSize.height");
  validatePositiveNumber(length, "cargoCellSize.length");
  validatePositiveNumber(width, "cargoCellSize.width");
  return { height, length, width };
}

function validateConfiguration({
  cargoCapacity,
  cargoCellSize,
  heightCellCount,
  lengthCellCount
}: StandardRackShelfConfiguration): StandardRackShelfConfiguration {
  return {
    cargoCapacity: validatePositiveInteger(cargoCapacity, "cargoCapacity"),
    cargoCellSize: validateCargoCellSize(cargoCellSize),
    heightCellCount: validatePositiveInteger(heightCellCount, "heightCellCount"),
    lengthCellCount: validatePositiveInteger(lengthCellCount, "lengthCellCount")
  };
}

function isSameCargoCellSize(left: CargoCellSize, right: CargoCellSize) {
  return left.height === right.height && left.length === right.length && left.width === right.width;
}

function validateCellCount(value: number, name: string) {
  return validatePositiveInteger(value, name);
}

function validateCellIndex(value: number, count: number, name: string) {
  if (!Number.isInteger(value) || value < 0 || value >= count) {
    throw new RangeError(`${name} must be an integer between 0 and ${count - 1}`);
  }
}

function validateSlotIndex(value: number, cargoCapacity: number) {
  if (!Number.isInteger(value) || value < 0 || value >= cargoCapacity) {
    throw new RangeError(`slotIndex must be an integer between 0 and ${cargoCapacity - 1}`);
  }
}

function createCargoPlacementKey({
  heightIndex,
  lengthIndex,
  slotIndex
}: StandardRackShelfCargoPlacement) {
  return `${heightIndex}:${lengthIndex}:${slotIndex}`;
}

function createCargoRoot(object: Object3D) {
  const root = new Group();
  root.add(object);
  root.updateWorldMatrix(true, true);

  const bounds = new Box3().setFromObject(object);
  const center = bounds.getCenter(new Vector3());

  object.position.x -= center.x;
  object.position.y -= bounds.min.y;
  object.position.z -= center.z;
  return root;
}

function validatePositiveNumber(value: number, name: string) {
  if (!Number.isFinite(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive finite number`);
  }
}

function validatePositiveInteger(value: number, name: string) {
  if (!Number.isInteger(value) || value <= 0) {
    throw new RangeError(`${name} must be a positive integer`);
  }

  return value;
}
